import { useState, useEffect } from 'react';
import { ArrowRight } from 'lucide-react';
import { supabase } from '../supabaseClient';
import { TopNav, Footer } from '../components/Chrome';

const TICKER = [
  { chem: 'LFP', cost: 70, delta: -2.8 },
  { chem: 'NMC532', cost: 85, delta: -1.9 },
  { chem: 'NMC622', cost: 88, delta: -2.1 },
  { chem: 'NCA', cost: 92, delta: -1.2 },
  { chem: 'NMC811', cost: 95, delta: -2.4 },
];

const FEATURES = [
  { t: 'Real-time pricing', d: 'Track cost trends across all major battery chemistries with historical data and predictive analytics.' },
  { t: 'Multi-level breakdowns', d: 'From pack level down to individual cathode materials, understand every cost component in detail.' },
  { t: 'Market intelligence', d: 'Stay informed with the latest industry news, supply chain insights, and market movements.' },
  { t: 'Chemistry comparison', d: 'Compare NMC811, NMC622, NMC532, LFP, and NCA technologies side by side.' },
  { t: 'Data export', d: 'Download comprehensive datasets for your own analysis and reporting needs.' },
  { t: 'Enterprise ready', d: 'Secure, scalable platform with tiered access for teams of any size.' },
];

const FALLBACK_NEWS = [
  { id: '1', title: 'Tesla Announces New Battery Technology Breakthrough', url: 'https://www.tesla.com/blog/battery-day', source: 'Tesla Blog' },
  { id: '2', title: 'LFP Batteries Gain Market Share in Electric Vehicles', url: 'https://www.reuters.com/business/autos-transportation/', source: 'Reuters' },
  { id: '3', title: 'Lithium Prices Continue Downward Trend', url: 'https://www.bloomberg.com/energy', source: 'Bloomberg' },
  { id: '4', title: 'New Gigafactory Opens in Southeast Asia', url: 'https://www.cnbc.com/energy/', source: 'CNBC' },
  { id: '5', title: 'Solid-State Battery Milestone Achieved', url: 'https://www.nature.com/subjects/batteries', source: 'Nature' },
  { id: '6', title: 'Government Announces EV Battery Recycling Initiative', url: 'https://www.energy.gov/eere/vehicles', source: 'U.S. DOE' },
];

export default function Landing({ navigate }) {
  const [news, setNews] = useState([]);

  useEffect(() => {
    (async () => {
      try {
        const { data, error } = await supabase
          .from('battery_news')
          .select('*')
          .order('published_at', { ascending: false })
          .limit(6);
        setNews(!error && data && data.length ? data : FALLBACK_NEWS);
      } catch {
        setNews(FALLBACK_NEWS);
      }
    })();
  }, []);

  return (
    <div>
      <TopNav navigate={navigate} variant="marketing" />

      {/* Hero */}
      <section className="hero">
        <div className="fade-up">
          <div className="eyebrow">Battery cost intelligence</div>
          <h1>The economics platform for EV batteries.</h1>
          <p className="sub">
            Rigorous cost analysis, market intelligence, and component-level breakdowns
            of lithium-ion technologies — built for consultants, researchers, policy
            teams, and industry decision-makers.
          </p>
          <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap' }}>
            <button className="btn btn-primary btn-lg" onClick={() => navigate('/signup')}>
              Get started free <ArrowRight size={17} />
            </button>
            <button className="btn btn-outline btn-lg" onClick={() => navigate('/login')}>
              Sign in
            </button>
          </div>
        </div>

        <div className="panel fade-up" style={{ overflow: 'hidden' }}>
          <div style={{ padding: '0.9rem 1rem', borderBottom: '1px solid var(--border)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <span style={{ fontSize: '0.8rem', fontWeight: 650, color: 'var(--muted)' }}>Pack cost by chemistry</span>
            <span className="badge badge-green">12-mo trend</span>
          </div>
          <table className="ticker-table mono">
            <thead>
              <tr><th>Chemistry</th><th>$/kWh</th><th>YoY</th></tr>
            </thead>
            <tbody>
              {TICKER.map((r) => (
                <tr key={r.chem}>
                  <td style={{ fontWeight: 600 }}>{r.chem}</td>
                  <td>${r.cost.toFixed(2)}</td>
                  <td className="delta-down">{r.delta.toFixed(1)}%</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      {/* Features */}
      <section className="section">
        <div className="section-head">
          <h2>Comprehensive cost intelligence</h2>
          <p>Everything you need to understand where battery cost comes from — and where it's headed.</p>
        </div>
        <div className="feature-grid">
          {FEATURES.map((f, i) => (
            <div className="feature-cell" key={f.t}>
              <div className="num">0{i + 1}</div>
              <h3>{f.t}</h3>
              <p>{f.d}</p>
            </div>
          ))}
        </div>
      </section>

      {/* News */}
      <section className="section" style={{ paddingTop: 0 }}>
        <div className="section-head" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', maxWidth: 'none' }}>
          <h2 style={{ fontSize: '1.5rem' }}>Latest industry news</h2>
          <span style={{ color: 'var(--faint)', fontSize: '0.8rem' }}>Updated daily · 7:00 AM IST</span>
        </div>
        <div>
          {news.map((a) => (
            <a className="news-row" key={a.id} href={a.url} target="_blank" rel="noopener noreferrer">
              <span className="t">{a.title}</span>
              <span className="s">{a.source}</span>
            </a>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section className="cta-band">
        <h2 style={{ fontSize: '1.8rem', marginBottom: '0.6rem' }}>Start analyzing battery costs today</h2>
        <p style={{ color: 'var(--muted)', marginBottom: '1.75rem' }}>
          Join the consultants, academics, and government analysts already using Lithix-cell.
        </p>
        <button className="btn btn-primary btn-lg" onClick={() => navigate('/signup')}>
          Create free account <ArrowRight size={17} />
        </button>
      </section>

      <Footer navigate={navigate} />
    </div>
  );
}
